import React, { CSSProperties, ForwardedRef, forwardRef, HTMLAttributes, ReactElement, useCallback } from 'react';
import cls from 'classnames';

import Icon from '@/common/components/Icon/Icon';

import actionStyles from './Action.module.css';
import styles from './TreeItemView.module.css';

export interface TreeItemProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  clone?: boolean;
  collapsed?: boolean;
  selected?: boolean;
  depth: number;
  disableDrag?: boolean;
  handleProps?: React.HTMLAttributes<HTMLButtonElement>;
  indentationWidth: number;
  item: T;
  onSelect?: () => void;
  contentClassName?: string;
  onCollapse?(): void;
  renderItem(item: T, props?: { handleProps: Any }): ReactElement;
  wrapperRef?(node: HTMLDivElement): void;
}

export const TreeItemView = forwardRef(<T,>(props: TreeItemProps<T>, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    clone,
    depth,
    selected,
    handleProps,
    indentationWidth,
    collapsed,
    onSelect,
    onCollapse,
    style,
    item,
    wrapperRef,
    renderItem,
    className,
    contentClassName,
    ...rest
  } = props;

  const handleCollapse = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      onCollapse?.();
    },
    [onCollapse],
  );

  return (
    <div
      aria-label="Tree item"
      className={cls(styles.wrapper, clone && styles.clone, className)}
      ref={wrapperRef}
      style={{ '--spacing': `${indentationWidth * depth}px` } as CSSProperties}
      {...rest}
      onClick={onSelect}
    >
      <div className={cls(styles.treeItem, contentClassName, selected && styles.selected)} ref={ref} style={style}>
        <button
          aria-label="Toggle tree item"
          onClick={handleCollapse}
          className={cls(
            styles.collapse,
            actionStyles.action,
            collapsed && styles.collapsed,
            !onCollapse && styles.hidden,
          )}
          tabIndex={0}
        >
          <Icon name="arrow-lt-right" />
        </button>

        {renderItem(item, { handleProps })}
      </div>
    </div>
  );
});
